import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shiftInstancesApi, staffApi, eventsApi, restaurantApi } from '../services/api';
import { formatShiftTime, wallClockRangeToISO } from '../lib/shiftTime';
import { toast } from '../stores/toastStore';
import {
  CalendarPlus,
  MapPin,
  Users,
  Tent,
  Plus,
  X,
  Check,
  Trash2,
  AlertTriangle,
  Inbox,
  Send,
  CalendarCheck,
  History,
  ClipboardList,
  Settings2,
  ChefHat,
  CircleDollarSign,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';

// ---- Local data shapes ----
interface StaffingSummaryRow {
  role: string;
  required: number;
  assigned: number;
  isFilled: boolean;
}

interface EventAssignment {
  id: string;
  employeeId: string;
  role: string;
  employee?: {
    firstName: string;
    lastName: string;
    primaryRole?: string;
  };
}

interface RoleRequirementRow {
  role: string;
  minCount: number;
  maxCount?: number;
}

interface EventInstance {
  id: string;
  name: string;
  date: string; // YYYY-MM-DD
  startTime: string; // ISO
  endTime: string; // ISO
  status: string;
  notes?: string;
  isEvent: boolean;
  eventType?: string;
  locationLabel?: string;
  expectedGuests?: number;
  roleRequirements?: RoleRequirementRow[];
  assignments?: EventAssignment[];
  staffingSummary?: StaffingSummaryRow[];
  totalRequired?: number;
  totalAssigned?: number;
  isFilled?: boolean;
  // Business layer
  eventStatus?: string;
  clientName?: string;
  clientEmail?: string;
  clientCompany?: string;
  spaceName?: string;
  depositPaid?: boolean;
  prepPlanned?: boolean;
  totals?: {
    menuSubtotalCents: number;
    roomFeeCents: number;
    serviceCents: number;
    taxCents: number;
    totalCents: number;
    depositCents: number;
  };
  spaceConflicts?: Array<{ id: string; name: string }>;
}

interface AvailableEmployee {
  id: string;
  firstName: string;
  lastName: string;
  primaryRole?: string;
}

const EVENT_TYPES = [
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'private_party', label: 'Private Party' },
  { value: 'catering', label: 'Catering' },
  { value: 'popup', label: 'Pop-up' },
  { value: 'collab', label: 'Collab' },
];

const ROLES = ['server', 'cook', 'bartender', 'host', 'busser'];

const eventTypeLabel = (type?: string) =>
  EVENT_TYPES.find((t) => t.value === type)?.label || type || 'Event';

const formatTime = (iso: string) => formatShiftTime(iso);

const formatDate = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

const toDateString = (d: Date) => d.toISOString().slice(0, 10);

const money = (cents: number) => {
  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars.toFixed(0)}` : `$${dollars.toFixed(2)}`;
};

export const EVENT_STATUS_META: Record<string, { label: string; className: string }> = {
  inquiry: { label: 'Inquiry', className: 'bg-amber-50 text-amber-700 border border-amber-200' },
  proposal_sent: { label: 'Proposal out', className: 'bg-blue-50 text-blue-700 border border-blue-200' },
  confirmed: { label: 'Confirmed', className: 'bg-success-50 text-success-700 border border-success-200' },
  completed: { label: 'Completed', className: 'bg-slate-100 text-slate-600 border border-slate-200' },
  cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-600 border border-red-200' },
};

const defaultFormData = {
  name: '',
  eventType: 'outdoor',
  date: toDateString(new Date()),
  startTime: '17:00',
  endTime: '22:00',
  locationLabel: '',
  expectedGuests: 0,
  roleRequirements: [{ role: 'server', minCount: 2 }] as RoleRequirementRow[],
};

const defaultInquiryData = {
  clientName: '',
  clientEmail: '',
  clientPhone: '',
  name: '',
  date: toDateString(new Date()),
  startTime: '18:00',
  endTime: '22:00',
  expectedGuests: 0,
  notes: '',
};

type PipelineTab = 'inquiries' | 'proposals' | 'confirmed' | 'past' | 'staffing';

const TABS: Array<{ key: PipelineTab; label: string; icon: any }> = [
  { key: 'inquiries', label: 'Inquiries', icon: Inbox },
  { key: 'proposals', label: 'Proposals out', icon: Send },
  { key: 'confirmed', label: 'Confirmed', icon: CalendarCheck },
  { key: 'past', label: 'Past', icon: History },
  { key: 'staffing', label: 'Staffing', icon: ClipboardList },
];

export default function EventsPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<PipelineTab>('confirmed');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInquiryModal, setShowInquiryModal] = useState(false);
  const [showSpacesModal, setShowSpacesModal] = useState(false);
  const [formData, setFormData] = useState(defaultFormData);
  const [formError, setFormError] = useState('');

  const today = new Date();
  const rangeEnd = new Date();
  rangeEnd.setDate(rangeEnd.getDate() + 60);
  const startDate = toDateString(today);
  const endDate = toDateString(rangeEnd);

  // Staffing view — the original upcoming-events list (unchanged behavior)
  const { data: events = [], isLoading } = useQuery({
    queryKey: ['events', startDate, endDate],
    queryFn: async () => {
      const response = await shiftInstancesApi.getByDateRange(startDate, endDate);
      const result = response.data?.data || response.data;
      const list: EventInstance[] = Array.isArray(result) ? result : [];
      return list
        .filter((s) => s.isEvent === true)
        .sort((a, b) => a.date.localeCompare(b.date));
    },
  });

  // Pipeline view — the business layer (inquiries → proposals → confirmed → past)
  const { data: pipelineEvents = [], isLoading: pipelineLoading } = useQuery({
    queryKey: ['events-pipeline'],
    queryFn: async () => {
      const response = await eventsApi.list();
      const result = response.data?.data || response.data;
      return (Array.isArray(result) ? result : []) as EventInstance[];
    },
  });

  // Fetch staff count for header context
  const { data: staffCount = 0 } = useQuery({
    queryKey: ['events-staff-count'],
    queryFn: async () => {
      const response = await staffApi.getAll();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result.length : 0;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) =>
      shiftInstancesApi.create({
        date: data.date,
        ...wallClockRangeToISO(data.date, data.startTime, data.endTime),
        name: data.name,
        isEvent: true,
        eventType: data.eventType,
        locationLabel: data.locationLabel || undefined,
        expectedGuests: data.expectedGuests || undefined,
        roleRequirements: data.roleRequirements
          .filter((r) => r.role && r.minCount > 0)
          .map((r) => ({ role: r.role, minCount: r.minCount })),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['events-pipeline'] });
      setShowAddModal(false);
      setFormData(defaultFormData);
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to create event');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => shiftInstancesApi.update(id, { status: 'cancelled' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['events-pipeline'] });
    },
  });

  const handleCancel = (event: EventInstance) => {
    if (window.confirm(`Cancel "${event.name}"? Assigned staff will be unscheduled.`)) {
      cancelMutation.mutate(event.id);
    }
  };

  const addRoleRequirement = () => {
    setFormData((prev) => ({
      ...prev,
      roleRequirements: [...prev.roleRequirements, { role: 'cook', minCount: 1 }],
    }));
  };

  const updateRoleRequirement = (idx: number, patch: Partial<RoleRequirementRow>) => {
    setFormData((prev) => ({
      ...prev,
      roleRequirements: prev.roleRequirements.map((r, i) =>
        i === idx ? { ...r, ...patch } : r
      ),
    }));
  };

  const removeRoleRequirement = (idx: number) => {
    setFormData((prev) => ({
      ...prev,
      roleRequirements: prev.roleRequirements.filter((_, i) => i !== idx),
    }));
  };

  const activeEvents = events.filter((e) => e.status !== 'cancelled');
  const todayStr = toDateString(new Date());

  const buckets = useMemo(() => {
    const inquiries: EventInstance[] = [];
    const proposals: EventInstance[] = [];
    const confirmed: EventInstance[] = [];
    const past: EventInstance[] = [];
    for (const e of pipelineEvents) {
      const status = e.eventStatus || 'confirmed';
      if (e.date < todayStr || status === 'completed' || status === 'cancelled') {
        past.push(e);
      } else if (status === 'inquiry') {
        inquiries.push(e);
      } else if (status === 'proposal_sent') {
        proposals.push(e);
      } else {
        confirmed.push(e);
      }
    }
    past.sort((a, b) => b.date.localeCompare(a.date));
    return { inquiries, proposals, confirmed, past };
  }, [pipelineEvents, todayStr]);

  const tabCount = (key: PipelineTab): number | null => {
    switch (key) {
      case 'inquiries': return buckets.inquiries.length;
      case 'proposals': return buckets.proposals.length;
      case 'confirmed': return buckets.confirmed.length;
      case 'past': return null;
      case 'staffing': return null;
    }
  };

  const pipelineList =
    activeTab === 'inquiries' ? buckets.inquiries :
    activeTab === 'proposals' ? buckets.proposals :
    activeTab === 'confirmed' ? buckets.confirmed :
    activeTab === 'past' ? buckets.past : [];

  const emptyCopy: Record<string, { title: string; sub: string }> = {
    inquiries: { title: 'No open inquiries', sub: 'When someone asks about hosting an event, log it here and nothing falls through the cracks.' },
    proposals: { title: 'No proposals out', sub: 'Send a proposal from an inquiry and it will wait here until the client confirms.' },
    confirmed: { title: 'No confirmed events coming up', sub: 'Confirmed events show up here with their staffing and prep status.' },
    past: { title: 'No past events yet', sub: 'Completed and cancelled events will be kept here for reference.' },
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Events</h1>
            <p className="text-sm md:text-base text-slate-500">
              From first inquiry to a staffed, prepped event.
            </p>
          </div>
          <HelpButton featureId="events" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowSpacesModal(true)}
            className="btn btn-secondary flex items-center justify-center gap-2 py-3 sm:py-2"
            title="Spaces & terms"
          >
            <Settings2 className="w-4 h-4" />
            <span className="hidden md:inline">Spaces &amp; terms</span>
          </button>
          <button
            onClick={() => setShowInquiryModal(true)}
            className="btn btn-primary flex items-center justify-center gap-2 flex-1 sm:flex-none py-3 sm:py-2"
          >
            <CalendarPlus className="w-5 h-5 sm:w-4 sm:h-4" />
            New inquiry
          </button>
        </div>
      </div>

      {/* Pipeline tabs */}
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200 -mx-4 px-4 md:mx-0 md:px-0">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const count = tabCount(tab.key);
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={clsx(
                'flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              )}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              {count !== null && count > 0 && (
                <span className={clsx(
                  'text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center',
                  activeTab === tab.key ? 'bg-primary-100 text-primary-700' : 'bg-slate-100 text-slate-500'
                )}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === 'staffing' ? (
        <>
          {/* Stats */}
          <div className="flex gap-3 md:gap-4">
            <div className="card p-3 md:p-4 flex-1">
              <p className="text-xs md:text-sm text-slate-500">Upcoming Events</p>
              <p className="text-xl md:text-2xl font-bold">{activeEvents.length}</p>
            </div>
            <div className="card p-3 md:p-4 flex-1">
              <p className="text-xs md:text-sm text-slate-500">Staff Available</p>
              <p className="text-xl md:text-2xl font-bold">{staffCount}</p>
            </div>
          </div>

          {/* Event list */}
          {isLoading ? (
            <div className="text-center py-12 text-slate-500">Loading...</div>
          ) : events.length === 0 ? (
            <div className="card text-center py-12 px-4">
              <Tent className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-700">No events scheduled yet</p>
              <p className="text-sm text-slate-500 mb-4">
                Got a pop-up, catering gig, or collab coming up? Set it up and staff it in minutes.
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="btn btn-primary inline-flex items-center gap-2"
              >
                <CalendarPlus className="w-4 h-4" />
                New Event
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowAddModal(true)}
                  className="btn btn-secondary inline-flex items-center gap-2"
                >
                  <CalendarPlus className="w-4 h-4" />
                  New staffing event
                </button>
              </div>
              {events.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onCancel={() => handleCancel(event)}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {pipelineLoading ? (
            <div className="text-center py-12 text-slate-500">Loading...</div>
          ) : pipelineList.length === 0 ? (
            <div className="card text-center py-12 px-4">
              <Tent className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-700">{emptyCopy[activeTab]?.title}</p>
              <p className="text-sm text-slate-500 mb-4">{emptyCopy[activeTab]?.sub}</p>
              {activeTab === 'inquiries' && (
                <button
                  onClick={() => setShowInquiryModal(true)}
                  className="btn btn-primary inline-flex items-center gap-2"
                >
                  <CalendarPlus className="w-4 h-4" />
                  New inquiry
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {pipelineList.map((event) => (
                <PipelineCard key={event.id} event={event} />
              ))}
            </div>
          )}
        </>
      )}

      {/* New Inquiry Modal */}
      {showInquiryModal && (
        <NewInquiryModal
          onClose={() => setShowInquiryModal(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['events-pipeline'] });
            queryClient.invalidateQueries({ queryKey: ['events'] });
            setShowInquiryModal(false);
            setActiveTab('inquiries');
          }}
        />
      )}

      {/* Spaces & terms Modal */}
      {showSpacesModal && (
        <SpacesTermsModal onClose={() => setShowSpacesModal(false)} />
      )}

      {/* New Event Modal (staffing) */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-lg max-h-[90vh] md:max-h-[85vh] overflow-y-auto p-5 md:p-6 pb-safe">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">New Event</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setFormData(defaultFormData);
                  setFormError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                createMutation.mutate(formData);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Event Name *
                  </label>
                  <input
                    type="text"
                    className="input w-full"
                    placeholder="e.g., Summer Patio Pop-up"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Event Type
                    </label>
                    <select
                      className="input w-full"
                      value={formData.eventType}
                      onChange={(e) =>
                        setFormData({ ...formData, eventType: e.target.value })
                      }
                    >
                      {EVENT_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      className="input w-full"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Start Time *
                    </label>
                    <input
                      type="time"
                      className="input w-full"
                      value={formData.startTime}
                      onChange={(e) =>
                        setFormData({ ...formData, startTime: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      End Time *
                    </label>
                    <input
                      type="time"
                      className="input w-full"
                      value={formData.endTime}
                      onChange={(e) =>
                        setFormData({ ...formData, endTime: e.target.value })
                      }
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Location
                    </label>
                    <input
                      type="text"
                      className="input w-full"
                      placeholder="e.g., Outdoor Patio"
                      value={formData.locationLabel}
                      onChange={(e) =>
                        setFormData({ ...formData, locationLabel: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Expected Guests
                    </label>
                    <input
                      type="number"
                      min={0}
                      className="input w-full"
                      value={formData.expectedGuests}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          expectedGuests: parseInt(e.target.value, 10) || 0,
                        })
                      }
                    />
                  </div>
                </div>

                {/* Role requirements */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-slate-700">Staff Needed</p>
                    <button
                      type="button"
                      onClick={addRoleRequirement}
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" />
                      Add role
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.roleRequirements.map((req, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <select
                          className="input flex-1 capitalize"
                          value={req.role}
                          onChange={(e) =>
                            updateRoleRequirement(idx, { role: e.target.value })
                          }
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r} className="capitalize">
                              {r}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min={1}
                          className="input w-20"
                          value={req.minCount}
                          onChange={(e) =>
                            updateRoleRequirement(idx, {
                              minCount: parseInt(e.target.value, 10) || 0,
                            })
                          }
                        />
                        <button
                          type="button"
                          onClick={() => removeRoleRequirement(idx)}
                          className="p-2 text-slate-400 hover:text-danger-500 hover:bg-slate-100 rounded-lg"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setFormData(defaultFormData);
                    setFormError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? 'Creating...' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Pipeline Card (business view) ----
function PipelineCard({ event }: { event: EventInstance }) {
  const navigate = useNavigate();
  const status = event.eventStatus || 'confirmed';
  const meta = EVENT_STATUS_META[status] || EVENT_STATUS_META.confirmed;
  const totals = event.totals;
  const hasConflict = (event.spaceConflicts?.length || 0) > 0;

  return (
    <button
      onClick={() => navigate(`/events/${event.id}`)}
      className="card p-4 md:p-5 w-full text-left hover:border-primary-300 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-semibold text-slate-900 text-base md:text-lg truncate">{event.name}</h2>
            <span className={clsx('text-xs font-medium px-2 py-0.5 rounded-full', meta.className)}>
              {meta.label}
            </span>
            {hasConflict && (
              <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">
                <AlertTriangle className="w-3 h-3" />
                Space conflict
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
            {event.clientName && (
              <span className="text-slate-700 font-medium">
                {event.clientName}
                {event.clientCompany ? ` · ${event.clientCompany}` : ''}
              </span>
            )}
            <span className="flex items-center gap-1">
              <CalendarPlus className="w-4 h-4" />
              {formatDate(event.date)}, {formatTime(event.startTime)} – {formatTime(event.endTime)}
            </span>
            {event.spaceName && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {event.spaceName}
              </span>
            )}
            {!!event.expectedGuests && (
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {event.expectedGuests} guests
              </span>
            )}
          </div>
        </div>
        {totals && totals.totalCents > 0 && (
          <div className="text-right flex-shrink-0">
            <p className="font-bold text-slate-900">{money(totals.totalCents)}</p>
            {totals.depositCents > 0 && (
              <p className={clsx('text-xs', event.depositPaid ? 'text-success-600' : 'text-slate-500')}>
                {event.depositPaid ? 'Deposit paid' : `Deposit ${money(totals.depositCents)}`}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Staffing + prep chips */}
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <span
          className={clsx(
            'inline-flex items-center gap-1 px-2 py-1 rounded-full',
            (event.totalRequired || 0) === 0
              ? 'bg-slate-100 text-slate-500'
              : event.isFilled
                ? 'bg-success-50 text-success-700'
                : 'bg-amber-50 text-amber-700'
          )}
        >
          <Users className="w-3 h-3" />
          {(event.totalRequired || 0) === 0
            ? 'No staffing plan yet'
            : `Staffing ${event.totalAssigned} of ${event.totalRequired} filled`}
        </span>
        <span
          className={clsx(
            'inline-flex items-center gap-1 px-2 py-1 rounded-full',
            event.prepPlanned ? 'bg-success-50 text-success-700' : 'bg-slate-100 text-slate-500'
          )}
        >
          <ChefHat className="w-3 h-3" />
          {event.prepPlanned ? 'Prep plan ✓' : 'Kitchen not planned'}
        </span>
        {totals && totals.depositCents > 0 && !event.depositPaid && status !== 'cancelled' && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-500">
            <CircleDollarSign className="w-3 h-3" />
            Deposit due
          </span>
        )}
      </div>
    </button>
  );
}

// ---- New Inquiry Modal ----
function NewInquiryModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [data, setData] = useState(defaultInquiryData);
  const [error, setError] = useState('');

  const createInquiry = useMutation({
    mutationFn: () =>
      eventsApi.createInquiry({
        clientName: data.clientName,
        clientEmail: data.clientEmail || undefined,
        clientPhone: data.clientPhone || undefined,
        name: data.name || undefined,
        date: data.date,
        startTime: data.startTime,
        endTime: data.endTime,
        expectedGuests: data.expectedGuests || undefined,
        notes: data.notes || undefined,
      }),
    onSuccess: () => {
      toast.success('Inquiry saved — it is waiting in your pipeline.');
      onCreated();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error?.message || 'Could not save this inquiry just now.');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-lg max-h-[90vh] md:max-h-[85vh] overflow-y-auto p-5 md:p-6 pb-safe">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold">New inquiry</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Just the essentials — you can build the menu and proposal after.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createInquiry.mutate();
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Client name *</label>
              <input
                type="text"
                className="input w-full"
                placeholder="e.g., Dana Rivera"
                value={data.clientName}
                onChange={(e) => setData({ ...data, clientName: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                <input
                  type="email"
                  className="input w-full"
                  value={data.clientEmail}
                  onChange={(e) => setData({ ...data, clientEmail: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                <input
                  type="tel"
                  className="input w-full"
                  value={data.clientPhone}
                  onChange={(e) => setData({ ...data, clientPhone: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Event name</label>
              <input
                type="text"
                className="input w-full"
                placeholder="e.g., Rivera rehearsal dinner (optional)"
                value={data.name}
                onChange={(e) => setData({ ...data, name: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
                <input
                  type="date"
                  className="input w-full"
                  value={data.date}
                  onChange={(e) => setData({ ...data, date: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start</label>
                <input
                  type="time"
                  className="input w-full"
                  value={data.startTime}
                  onChange={(e) => setData({ ...data, startTime: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End</label>
                <input
                  type="time"
                  className="input w-full"
                  value={data.endTime}
                  onChange={(e) => setData({ ...data, endTime: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Expected guests</label>
              <input
                type="number"
                min={0}
                className="input w-full"
                value={data.expectedGuests}
                onChange={(e) => setData({ ...data, expectedGuests: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
              <textarea
                className="input w-full"
                rows={3}
                placeholder="What they asked for, budget hints, anything worth remembering."
                value={data.notes}
                onChange={(e) => setData({ ...data, notes: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={createInquiry.isPending}>
              {createInquiry.isPending ? 'Saving...' : 'Save inquiry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---- Spaces & Terms Modal (settings.events) ----
function SpacesTermsModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [spaces, setSpaces] = useState<Array<{ name: string; capacity: number }>>([]);
  const [terms, setTerms] = useState('');
  const [loaded, setLoaded] = useState(false);

  useQuery({
    queryKey: ['events-settings'],
    queryFn: async () => {
      const response = await restaurantApi.getCurrent();
      const settings = response.data?.data?.settings || {};
      const ev = settings.events || {};
      if (!loaded) {
        setSpaces(Array.isArray(ev.spaces) ? ev.spaces.map((s: any) => ({ name: String(s.name || ''), capacity: Number(s.capacity) || 0 })) : []);
        setTerms(typeof ev.terms === 'string' ? ev.terms : '');
        setLoaded(true);
      }
      return settings;
    },
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      restaurantApi.updateSettings({
        events: {
          spaces: spaces.filter((s) => s.name.trim()).map((s) => ({ name: s.name.trim(), capacity: s.capacity || 0 })),
          terms,
        },
      }),
    onSuccess: () => {
      toast.success('Spaces & terms saved.');
      queryClient.invalidateQueries({ queryKey: ['events-settings'] });
      queryClient.invalidateQueries({ queryKey: ['restaurant'] });
      onClose();
    },
    onError: () => {
      toast.error('Could not save just now — please try again.');
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-lg max-h-[90vh] md:max-h-[85vh] overflow-y-auto p-5 md:p-6 pb-safe">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold">Spaces &amp; terms</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Your bookable rooms and the standard terms that go on every proposal.
        </p>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-700">Spaces</p>
              <button
                type="button"
                onClick={() => setSpaces((prev) => [...prev, { name: '', capacity: 0 }])}
                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add space
              </button>
            </div>
            {spaces.length === 0 ? (
              <p className="text-sm text-slate-400">No spaces yet — add your private room, patio, or full buyout.</p>
            ) : (
              <div className="space-y-2">
                {spaces.map((space, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      type="text"
                      className="input flex-1"
                      placeholder="e.g., Back Room"
                      value={space.name}
                      onChange={(e) =>
                        setSpaces((prev) => prev.map((s, i) => (i === idx ? { ...s, name: e.target.value } : s)))
                      }
                    />
                    <input
                      type="number"
                      min={0}
                      className="input w-24"
                      placeholder="Seats"
                      title="Capacity"
                      value={space.capacity || ''}
                      onChange={(e) =>
                        setSpaces((prev) =>
                          prev.map((s, i) => (i === idx ? { ...s, capacity: parseInt(e.target.value, 10) || 0 } : s))
                        )
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setSpaces((prev) => prev.filter((_, i) => i !== idx))}
                      className="p-2 text-slate-400 hover:text-danger-500 hover:bg-slate-100 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Proposal terms</label>
            <textarea
              className="input w-full"
              rows={5}
              placeholder={'e.g., A 50% deposit reserves your date. Final guest count is due 72 hours before the event. Cancellations within 7 days forfeit the deposit.'}
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            className="btn btn-primary"
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Event Card (staffing view) ----
interface EventCardProps {
  event: EventInstance;
  onCancel: () => void;
}

function EventCard({ event, onCancel }: EventCardProps) {
  const queryClient = useQueryClient();
  const [addRole, setAddRole] = useState('');
  const isCancelled = event.status === 'cancelled';

  // Available employees for the chosen role
  const { data: available = [], isFetching } = useQuery({
    queryKey: ['event-available', event.id, addRole],
    queryFn: async () => {
      const response = await shiftInstancesApi.getAvailableEmployees(event.id, addRole);
      const result = response.data?.data || response.data;
      return (Array.isArray(result) ? result : []) as AvailableEmployee[];
    },
    enabled: !!addRole && !isCancelled,
  });

  const assignMutation = useMutation({
    mutationFn: (vars: { employeeId: string; override?: boolean }) =>
      shiftInstancesApi.addAssignment(event.id, {
        employeeId: vars.employeeId,
        role: addRole,
        scheduledStart: event.startTime,
        scheduledEnd: event.endTime,
        override: vars.override,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event-available', event.id] });
    },
    onError: (err: any, vars) => {
      const e = err?.response?.data?.error;
      // Time-off / availability conflicts can be overridden by the manager.
      if (e?.canOverride && !vars.override) {
        if (window.confirm(`${e.message}\n\nAssign anyway?`)) {
          assignMutation.mutate({ employeeId: vars.employeeId, override: true });
        }
        return;
      }
      toast.error(e?.message || 'Failed to assign staff to this event.');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (assignmentId: string) =>
      shiftInstancesApi.removeAssignment(event.id, assignmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['event-available', event.id] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to remove this assignment.');
    },
  });

  const summary = event.staffingSummary || [];
  const assignments = event.assignments || [];

  return (
    <div className={clsx('card p-4 md:p-5', isCancelled && 'opacity-60')}>
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-semibold text-slate-900 text-lg truncate">{event.name}</h2>
            <span className="badge badge-info">{eventTypeLabel(event.eventType)}</span>
            {isCancelled && <span className="badge badge-danger">Cancelled</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
            <span className="flex items-center gap-1">
              <CalendarPlus className="w-4 h-4" />
              {formatDate(event.date)}, {formatTime(event.startTime)} – {formatTime(event.endTime)}
            </span>
            {event.locationLabel && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {event.locationLabel}
              </span>
            )}
            {!!event.expectedGuests && (
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                {event.expectedGuests} guests
              </span>
            )}
          </div>
        </div>
        {!isCancelled && (
          <button
            onClick={onCancel}
            className="p-2 text-slate-400 hover:text-danger-500 hover:bg-slate-100 rounded-lg flex-shrink-0"
            title="Cancel event"
          >
            <Trash2 className="w-5 h-5" />
          </button>
        )}
      </div>

      {!isCancelled && (
        <>
          {/* Staffing summary */}
          {summary.length > 0 && (
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {summary.map((row) => (
                <div
                  key={row.role}
                  className={clsx(
                    'rounded-lg border px-3 py-2',
                    row.isFilled
                      ? 'bg-success-50 border-success-200'
                      : 'bg-red-50 border-red-200'
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-600 capitalize">
                      {row.role}
                    </span>
                    {row.isFilled ? (
                      <Check className="w-4 h-4 text-success-600" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                  <p
                    className={clsx(
                      'text-lg font-bold',
                      row.isFilled ? 'text-success-700' : 'text-red-600'
                    )}
                  >
                    {row.assigned}/{row.required}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Assigned staff chips */}
          {assignments.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium text-slate-500 mb-2">Assigned</p>
              <div className="flex flex-wrap gap-2">
                {assignments.map((a) => (
                  <span
                    key={a.id}
                    className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 bg-slate-100 rounded-full text-sm text-slate-700"
                  >
                    <span>
                      {a.employee
                        ? `${a.employee.firstName} ${a.employee.lastName}`
                        : 'Staff'}
                      <span className="text-slate-400 capitalize"> · {a.role}</span>
                    </span>
                    <button
                      onClick={() => removeMutation.mutate(a.id)}
                      className="p-0.5 rounded-full hover:bg-slate-300/60 text-slate-500"
                      title="Remove"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Add staff */}
          <div className="mt-4 border-t pt-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <select
                className="input w-auto min-w-[140px] capitalize"
                value={addRole}
                onChange={(e) => setAddRole(e.target.value)}
              >
                <option value="">Add staff…</option>
                {ROLES.map((r) => (
                  <option key={r} value={r} className="capitalize">
                    {r}
                  </option>
                ))}
              </select>
            </div>
            {addRole && (
              <div className="mt-3">
                {isFetching ? (
                  <p className="text-sm text-slate-400">Finding available staff…</p>
                ) : available.length === 0 ? (
                  <p className="text-sm text-slate-400">
                    No available {addRole}s for this time.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {available.map((emp) => (
                      <button
                        key={emp.id}
                        onClick={() => assignMutation.mutate({ employeeId: emp.id })}
                        disabled={assignMutation.isPending}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm hover:bg-primary-100 transition-colors disabled:opacity-50"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        {emp.firstName} {emp.lastName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
